import { useQuery } from '@tanstack/react-query'
import type { ListEntriesQuery } from '@innerloopco/loopcms-core'
import { api } from './api.js'

/** One place for query keys, so invalidation in the editor hits the browser's cache. */
export const queryKeys = {
  entries: (query: ListEntriesQuery) => ['entries', query] as const,
  entry: (id: string) => ['entry', id] as const,
  blocks: (entryId: string) => ['blocks', entryId] as const,
  schema: () => ['schema'] as const,
  health: () => ['health'] as const,
  siteMap: () => ['siteMap'] as const,
  /** Not parameterized by page — `useMediaLibrary` accumulates pages itself
   * via `useInfiniteQuery`, so there is exactly one cache entry to
   * invalidate after an upload or a delete, not one per page fetched. */
  media: () => ['media'] as const,
  blockDefs: () => ['blockDefs'] as const,
  entryCounts: (query: Pick<ListEntriesQuery, 'collection' | 'q'>) => ['entryCounts', query] as const,
  versions: (entryId: string) => ['versions', entryId] as const,
  version: (entryId: string, versionId: string) => ['version', entryId, versionId] as const,
}

export const useEntries = (query: ListEntriesQuery) =>
  useQuery({ queryKey: queryKeys.entries(query), queryFn: () => api.listEntries(query) })

export const useEntry = (id: string) =>
  useQuery({ queryKey: queryKeys.entry(id), queryFn: () => api.getEntry(id) })

// No structural sharing: a structural save writes the server's answer into
// this cache by reference, and the editor's load effect recognises that
// exact array as "already reconciled" (see EntryEditor).
export const useBlocks = (entryId: string) =>
  useQuery({ queryKey: queryKeys.blocks(entryId), queryFn: () => api.getBlocks(entryId), structuralSharing: false })

export const useSchema = () =>
  useQuery({ queryKey: queryKeys.schema(), queryFn: () => api.getSchema() })

// The site's own base URL never changes without a server restart, so this is
// worth caching for the life of the tab — the editor's Site preview reads it
// every time the tab opens.
export const useHealth = () =>
  useQuery({ queryKey: queryKeys.health(), queryFn: () => api.health(), staleTime: Infinity })

/** The custom block registry (live only for editors; admins also get in_development). */
export const useBlockDefs = () =>
  useQuery({ queryKey: queryKeys.blockDefs(), queryFn: () => api.listBlockDefs(), staleTime: 60_000 })
export const useEntryCounts = (query: Pick<ListEntriesQuery, 'collection' | 'q'>) =>
  useQuery({ queryKey: queryKeys.entryCounts(query), queryFn: () => api.listEntryCounts(query) })

export const useVersions = (entryId: string) =>
  useQuery({ queryKey: queryKeys.versions(entryId), queryFn: () => api.listVersions(entryId) })

export const useVersion = (entryId: string, versionId: string | null) =>
  useQuery({
    queryKey: queryKeys.version(entryId, versionId ?? ''),
    queryFn: () => api.getVersion(entryId, versionId as string),
    enabled: versionId !== null,
  })
